'use client';

import type { SwiperClass, SwiperRef } from 'swiper/react';

import React from 'react';
import { ArrowLeft, ArrowRight } from 'react-feather';
import { Container, Grid, GridItem, Icon, Stack, Text } from '@chakra-ui/react';
import Image from 'next/image';

import 'swiper/css';

import { SwiperGallery } from '..';

import { IconButton } from '@/components';
import { useTranslation } from '@/hooks';
import Gallery1 from '@/images/aboutUs/gallery-1.jpg';
import Gallery2 from '@/images/aboutUs/gallery-2.jpg';
import Gallery3 from '@/images/aboutUs/gallery-3.jpg';
import Gallery4 from '@/images/aboutUs/gallery-4.jpg';
import Gallery5 from '@/images/aboutUs/gallery-5.jpg';
import Gallery6 from '@/images/aboutUs/gallery-6.jpg';
import Gallery7 from '@/images/aboutUs/gallery-7.jpg';

const OurTeam: React.FC = () => {
  const { t } = useTranslation('pages.aboutUs');
  const swiperRef = React.useRef<SwiperRef>(null);
  const [progress, setProgress] = React.useState(0);

  const prevSlide = () => {
    swiperRef.current?.swiper.slidePrev();
  };

  const nextSlide = () => {
    swiperRef.current?.swiper.slideNext();
  };

  const handleSlideChange = (event: SwiperClass) => {
    setProgress(event.progress);
  };

  const gridContent = [
    { id: 1, image: Gallery1, gridArea: '1 / 1 / 4 / 4' },
    { id: 2, image: Gallery2, gridArea: '4 / 1 / 7 / 4' },
    { id: 3, image: Gallery3, gridArea: '3 / 4 / 6 / 7' },
    { id: 4, image: Gallery4, gridArea: '1 / 7 / 4 / 10' },
    { id: 5, image: Gallery5, gridArea: '4 / 10 / 7 / 13' },
    { id: 6, image: Gallery6, gridArea: '1 / 10 / 4 / 13' },
    { id: 7, image: Gallery7, gridArea: '3 / 13 / 6 / 16' },
  ];

  return (
    <Container>
      <Stack mt={16} mb={20} w="100%" flexDir="column">
        <Stack
          flexDir="column"
          justify="center"
          align={{ base: 'center', lg: 'flex-start' }}
          gap={4}
        >
          <Text fontWeight={800} fontSize="3xl">
            {t('pictures')}
          </Text>
          <Text fontWeight={700} fontSize="2xl">
            {t('ourTeam')}
          </Text>
          <Grid
            display={{ base: 'none', lg: 'grid' }}
            gridTemplateColumns="repeat(15, 1fr)"
            gridTemplateRows="repeat(6, 1fr)"
            gridColumnGap="10px"
            gridRowGap="10px"
          >
            {gridContent.map(({ id, gridArea, image }) => (
              <GridItem key={id} gridArea={gridArea}>
                <Image
                  src={image}
                  alt="welcome"
                  style={{
                    width: '2570px',
                    height: '247px',
                    objectFit: 'cover',
                    borderRadius: 18,
                  }}
                />
              </GridItem>
            ))}
          </Grid>
          <Stack
            display={{ base: 'flex', lg: 'none' }}
            align="center"
            justify="center"
          >
            <Stack align="center" justify="center">
              <SwiperGallery
                images={gridContent.map((item) => item.image)}
                swiperRef={swiperRef}
                handleSlideChange={handleSlideChange}
              />
            </Stack>
            <Stack
              w="100%"
              flexDir="row"
              align="center"
              justify="center"
              mt={4}
            >
              <IconButton
                isDisabled={progress === 0}
                backgroundColor="primary.200"
                aria-label="previous"
                icon={<Icon boxSize="Arrow" as={ArrowRight} stroke={'text'} />}
                mx={2}
                onClick={() => {
                  prevSlide();
                }}
              />
              <IconButton
                isDisabled={progress === 1}
                backgroundColor="primary.200"
                aria-label="next"
                icon={<Icon boxSize="6" as={ArrowLeft} stroke={'text'} />}
                mx={2}
                onClick={() => {
                  nextSlide();
                }}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
};

export default OurTeam;
